// Button.tsx
import { useNavigate } from "@remix-run/react";
import React from "react";
import { Link } from "react-router-dom";

// Utility to call Plausible
const trackPlausibleEvent = (
  eventName: string,
  props?: Record<string, any>,
) => {
  if (typeof window !== "undefined" && (window as any).plausible) {
    (window as any).plausible(eventName, { props });
  }
};

export interface ButtonProps {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  children?: React.ReactNode;
  variant?: "primary" | "secondary" | "outlined" | "default";
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  loading?: boolean;
  className?: string;
  disabled?: boolean;
  to?: string;
  /**
   * Name of the custom Plausible event to fire on click.
   * If not provided, no event is fired.
   */
  plausibleEventName?: string;
  /**
   * Optional properties to include with the Plausible event.
   */
  plausibleEventProps?: Record<string, any>;
}

export const Button = ({
  onClick,
  children,
  variant,
  size = "md",
  loading,
  className,
  disabled,
  to,
  plausibleEventName,
  plausibleEventProps,
  ...props
}: ButtonProps) => {
  const navigate = useNavigate();
  let sizeClass = "px-3.5 py-2.5 text-sm";
  switch (size) {
    case "xs":
      sizeClass = "py-1 px-2 text-xs";
      break;
    case "sm":
      sizeClass = "py-1 px-2 text-sm";
      break;
    case "md":
      sizeClass = "py-1.5 px-2.5 text-md";
      break;
    case "lg":
      sizeClass = "py-2 px-3 text-md";
      break;
    case "xl":
      sizeClass = "py-2.5 px-3.5 text-lg";
      break;
  }

  let variantClass = "text-gray-900 bg-lime-500 hover:bg-lime-400";
  if (variant === "secondary") {
    variantClass = "bg-gray-800/50 hover:bg-gray-800/60";
  } else if (variant === "outlined") {
    variantClass =
      "bg-gray-800 border-2 border-lime-500 hover:bg-lime-400 text-gray-50 hover:text-gray-900";
  }

  const combinedClassName = `${variantClass} ${
    disabled ? "cursor-not-allowed opacity-30" : "cursor-pointer"
  } ${
    className || ""
  } rounded-lg text-center font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime-500 ${sizeClass}`;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (plausibleEventName) {
      trackPlausibleEvent(plausibleEventName, plausibleEventProps);
    }

    if (onClick) {
      onClick(event);
    }

    if (to) {
      event.preventDefault();
      if (to.startsWith("http")) {
        window.open(to, "_blank");
      } else {
        navigate(to);
      }
    }
  };

  if (to && !onClick && !plausibleEventName) {
    return (
      <Link to={to} className={combinedClassName} {...props}>
        {loading ? "Submitting..." : children}
      </Link>
    );
  }

  // Otherwise render a <button>
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={combinedClassName}
      onClick={handleClick}
      {...props}
    >
      {loading ? "Submitting..." : children}
    </button>
  );
};
